import { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronUp, IconTrash } from '@tabler/icons-react';

import { JURISDICTIONS } from '@/constants';
import {
  DataRequirement,
  DataRequirementApi,
  DataRequirementType,
  DimensionCondition,
  DimensionMode,
  ScopeComponentCapability,
  ScopeComponentCapabilityCreateRequest,
  ScopeComponentCapabilityUpdateRequest,
} from '@/types';

interface CapabilityEditorModalProps {
  opened: boolean;
  onClose: () => void;
  /** When set, the modal is in edit mode. */
  capability: ScopeComponentCapability | null;
  /** Available providers for the API data_requirement type. */
  providers: string[];
  /** Available type_of_check values for the API data_requirement type. */
  typeOfChecks: string[];
  /** provider -> the checks it can serve. Constrains the provider picker. */
  providerChecks: Record<string, string[]>;
  onSubmit: (
    data: ScopeComponentCapabilityCreateRequest | ScopeComponentCapabilityUpdateRequest
  ) => Promise<void>;
}

/**
 * An API row as authored: one check type fulfilled by one or more providers.
 *
 * The wire format is one requirement per provider. Collapsing them here is what
 * makes "which providers fulfil this" a single decision rather than a row each,
 * and `expandApiRow` puts them back before submitting.
 *
 * `providerConfig` is carried through untouched — it is not editable here, and
 * rows differing on it are deliberately never merged (see `collapseRequirements`).
 */
interface ApiRow {
  kind: 'api';
  typeOfCheck: string;
  providers: string[];
  providerConfig: Record<string, unknown>;
  scopeComponentSlug?: string | null;
}

/** Any non-API requirement, held as-is. */
interface PlainRow {
  kind: 'plain';
  requirement: Exclude<DataRequirement, DataRequirementApi>;
}

type EditorRow = ApiRow | PlainRow;

const rowType = (row: EditorRow): DataRequirementType =>
  row.kind === 'api' ? 'api' : row.requirement.type;

/** Stable identity for grouping API rows: same check AND same provider_config. */
function apiRowKey(req: DataRequirementApi): string {
  return `${req.type_of_check}::${JSON.stringify(req.provider_config ?? {})}::${
    req.scope_component_slug ?? ''
  }`;
}

/**
 * Fold stored requirements into editor rows.
 *
 * API requirements sharing a check type merge into one row only when their
 * `provider_config` matches too. Components that route data by subject type
 * (corporate-info's two Moody's entries) rely on that: merging them would erase
 * the routing on the next save.
 */
export function collapseRequirements(requirements: DataRequirement[]): EditorRow[] {
  const rows: EditorRow[] = [];
  const apiRowsByKey = new Map<string, ApiRow>();

  for (const req of requirements) {
    if (req.type !== 'api') {
      rows.push({ kind: 'plain', requirement: req });
      continue;
    }
    const key = apiRowKey(req);
    const existing = apiRowsByKey.get(key);
    if (existing) {
      if (!existing.providers.includes(req.provider)) {
        existing.providers.push(req.provider);
      }
      continue;
    }
    const row: ApiRow = {
      kind: 'api',
      typeOfCheck: req.type_of_check,
      providers: req.provider ? [req.provider] : [],
      providerConfig: req.provider_config ?? {},
      scopeComponentSlug: req.scope_component_slug,
    };
    apiRowsByKey.set(key, row);
    rows.push(row);
  }

  return rows;
}

/** One requirement per provider — the shape the backend and plan builder use. */
export function expandRows(rows: EditorRow[]): DataRequirement[] {
  return rows.flatMap((row): DataRequirement[] => {
    if (row.kind === 'plain') {
      return [row.requirement];
    }
    return row.providers.map((provider) => ({
      type: 'api',
      provider,
      type_of_check: row.typeOfCheck,
      provider_config: row.providerConfig,
      ...(row.scopeComponentSlug !== undefined
        ? { scope_component_slug: row.scopeComponentSlug }
        : {}),
    }));
  });
}

/** Providers that can serve `typeOfCheck`; all of them before one is chosen. */
export function providersForCheck(
  typeOfCheck: string,
  providers: string[],
  providerChecks: Record<string, string[]>
): string[] {
  if (!typeOfCheck) {
    return providers;
  }
  const capable = providers.filter((p) => providerChecks[p]?.includes(typeOfCheck));
  // An unrecognised check type (or a catalogue that failed to load) shouldn't
  // leave the picker empty with no way forward.
  return capable.length > 0 ? capable : providers;
}

const TYPE_OPTIONS: Array<{ value: DataRequirementType; label: string }> = [
  { value: 'api', label: 'API' },
  { value: 'human', label: 'Manual / Human' },
  { value: 'metadata', label: 'Subject metadata' },
  { value: 'scope_component', label: 'Synthesis (depends on other components)' },
];

const MODE_OPTIONS: Array<{ value: DimensionMode; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'includes', label: 'Includes only' },
  { value: 'excludes', label: 'Excludes' },
];

const ALL_CONDITION: DimensionCondition = { mode: 'all', values: [] };

function emptyRow(type: DataRequirementType): EditorRow {
  switch (type) {
    case 'api':
      return { kind: 'api', typeOfCheck: '', providers: [], providerConfig: {} };
    case 'human':
      return { kind: 'plain', requirement: { type: 'human', description: '' } };
    case 'metadata':
      return { kind: 'plain', requirement: { type: 'metadata', field: null } };
    case 'scope_component':
      return { kind: 'plain', requirement: { type: 'scope_component' } };
  }
}

interface RequirementFieldsProps {
  row: EditorRow;
  providers: string[];
  typeOfChecks: string[];
  providerChecks: Record<string, string[]>;
  onChange: (next: EditorRow) => void;
}

function RequirementFields({
  row,
  providers,
  typeOfChecks,
  providerChecks,
  onChange,
}: Readonly<RequirementFieldsProps>) {
  if (row.kind === 'api') {
    const available = providersForCheck(row.typeOfCheck, providers, providerChecks);
    return (
      <Stack gap="xs">
        <Select
          label="Type of check"
          placeholder="Select check type"
          data={typeOfChecks.map((t) => ({ value: t, label: t }))}
          value={row.typeOfCheck}
          onChange={(value) => {
            const typeOfCheck = value ?? '';
            const stillValid = providersForCheck(typeOfCheck, providers, providerChecks);
            onChange({
              ...row,
              typeOfCheck,
              // Drop providers that can't serve the new check rather than
              // carrying a selection the server would reject on save.
              providers: row.providers.filter((p) => stillValid.includes(p)),
            });
          }}
          searchable
          required
        />
        <MultiSelect
          label="Providers"
          placeholder={row.typeOfCheck ? 'Select providers' : 'Choose a check type first'}
          description="Every selected provider runs for this check."
          data={available.map((p) => ({ value: p, label: p }))}
          value={row.providers}
          onChange={(next) => onChange({ ...row, providers: next })}
          disabled={!row.typeOfCheck}
          searchable
          clearable
          required
        />
      </Stack>
    );
  }
  const { requirement } = row;
  if (requirement.type === 'human') {
    return (
      <Textarea
        label="Description"
        placeholder="Describe the manual work needed"
        value={requirement.description ?? ''}
        onChange={(e) =>
          onChange({
            kind: 'plain',
            requirement: { ...requirement, description: e.currentTarget.value || null },
          })
        }
        autosize
        minRows={2}
      />
    );
  }
  if (requirement.type === 'metadata') {
    return (
      <TextInput
        label="Specific metadata field (optional)"
        placeholder="Leave blank for full subject metadata"
        value={requirement.field ?? ''}
        onChange={(e) =>
          onChange({
            kind: 'plain',
            requirement: { ...requirement, field: e.currentTarget.value || null },
          })
        }
      />
    );
  }
  // scope_component — no fields
  return (
    <Text size="sm" c="dimmed">
      Synthesis requirements are fulfilled by the component&apos;s declared dependencies. No
      additional fields needed.
    </Text>
  );
}

interface DimensionEditorProps {
  label: string;
  condition: DimensionCondition;
  onChange: (next: DimensionCondition) => void;
}

function JurisdictionDimensionEditor({
  label,
  condition,
  onChange,
}: Readonly<DimensionEditorProps>) {
  const options = useMemo(
    () => JURISDICTIONS.map((c) => ({ value: c.alpha2code, label: `${c.name} (${c.alpha2code})` })),
    []
  );

  return (
    <Stack gap="xs">
      <Select
        label={`${label} mode`}
        data={MODE_OPTIONS}
        value={condition.mode}
        onChange={(value) =>
          onChange({
            mode: (value as DimensionMode) ?? 'all',
            values: value === 'all' ? [] : condition.values,
          })
        }
        allowDeselect={false}
      />
      {condition.mode !== 'all' && (
        <MultiSelect
          label={`${label} values`}
          placeholder="Select jurisdictions"
          data={options}
          value={condition.values}
          onChange={(values) => onChange({ ...condition, values })}
          searchable
          clearable
        />
      )}
    </Stack>
  );
}

export function CapabilityEditorModal({
  opened,
  onClose,
  capability,
  providers,
  typeOfChecks,
  providerChecks,
  onSubmit,
}: Readonly<CapabilityEditorModalProps>) {
  const [rows, setRows] = useState<EditorRow[]>([]);
  const [jurisdictions, setJurisdictions] = useState<DimensionCondition>(ALL_CONDITION);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = capability !== null;

  // Reset state when modal opens or capability changes
  useEffect(() => {
    if (!opened) {
      return;
    }
    if (capability) {
      setRows(collapseRequirements(capability.dataRequirements));
      setJurisdictions(capability.jurisdictions ?? ALL_CONDITION);
      setShowAdvanced(
        (capability.jurisdictions ?? ALL_CONDITION).mode !== 'all' ||
          (capability.jurisdictions?.values?.length ?? 0) > 0
      );
    } else {
      setRows([emptyRow('api')]);
      setJurisdictions(ALL_CONDITION);
      setShowAdvanced(false);
    }
    setError(null);
  }, [opened, capability]);

  const updateRow = (index: number, next: EditorRow) => {
    setRows((prev) => prev.map((r, i) => (i === index ? next : r)));
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow('api')]);
  };

  const handleTypeChange = (index: number, type: DataRequirementType) => {
    updateRow(index, emptyRow(type));
  };

  const validate = (): string | null => {
    if (rows.length === 0) {
      return 'At least one data requirement is required.';
    }
    for (const [i, row] of rows.entries()) {
      if (row.kind !== 'api') {
        continue;
      }
      if (!row.typeOfCheck) {
        return `Requirement #${i + 1}: select a type of check.`;
      }
      if (row.providers.length === 0) {
        return `Requirement #${i + 1}: select at least one provider.`;
      }
    }
    if (jurisdictions.mode !== 'all' && jurisdictions.values.length === 0) {
      return 'Select at least one jurisdiction value (or switch the mode back to "All").';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        data_requirements: expandRows(rows),
        jurisdictions,
        // subjurisdictions UI not yet exposed; backend accepts null
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save capability');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit capability' : 'Add capability'}
      size="lg"
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>Data requirements</Title>
          <Text size="sm" c="dimmed">
            Each requirement describes one piece of data this capability provides.
          </Text>
        </Stack>

        {rows.map((row, index) => (
          <Stack
            key={index}
            gap="xs"
            p="sm"
            style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}
          >
            <Group justify="space-between" align="center">
              <Select
                label="Type"
                data={TYPE_OPTIONS}
                value={rowType(row)}
                onChange={(value) =>
                  handleTypeChange(index, (value as DataRequirementType) ?? 'api')
                }
                allowDeselect={false}
                w={260}
              />
              {rows.length > 1 && (
                <ActionIcon
                  color="red"
                  variant="subtle"
                  aria-label={`Remove requirement ${index + 1}`}
                  onClick={() => removeRow(index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>
            <RequirementFields
              row={row}
              providers={providers}
              typeOfChecks={typeOfChecks}
              providerChecks={providerChecks}
              onChange={(next) => updateRow(index, next)}
            />
          </Stack>
        ))}

        <Group justify="flex-start">
          <Button variant="default" size="xs" onClick={addRow}>
            Add another requirement
          </Button>
        </Group>

        <Divider />

        <Group justify="space-between" align="center">
          <Title order={5}>Conditions</Title>
          <Button
            variant="subtle"
            size="xs"
            rightSection={
              showAdvanced ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
            }
            onClick={() => setShowAdvanced((v) => !v)}
          >
            {showAdvanced ? 'Hide jurisdiction filter' : 'Add jurisdiction filter'}
          </Button>
        </Group>

        {!showAdvanced && (
          <Text size="sm" c="dimmed">
            Applies to all jurisdictions and subjurisdictions.
          </Text>
        )}

        <Collapse in={showAdvanced}>
          <JurisdictionDimensionEditor
            label="Jurisdictions"
            condition={jurisdictions}
            onChange={setJurisdictions}
          />
        </Collapse>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {isEdit ? 'Save changes' : 'Add capability'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
